import { useCallback, useEffect, useMemo, useState } from "react";
import { table } from "./store.ts";
import type { PresentationFrame, TableSnapshot } from "./store.ts";
import type { CardBrief, GameEvent } from "./types.ts";

const CARD_EVENTS: Record<string, true> = {
  card_drawn: true, card_resolved: true, klein_drawn: true, klein_given: true,
  klein_resolved: true, equipment_gained: true, equipment_lost: true,
};
const NO_EVENTS: GameEvent[] = [];
type AttackRoll = { event: GameEvent; outcomes: GameEvent[] };
type Scene = { event: GameEvent | null; cards: GameEvent[] | null; through: number; duration: number; settleEquipment?: boolean; diceOutcomes?: GameEvent[] };

function playersAt(frame: PresentationFrame, through: number, settled = false) {
  if (!frame.before || settled) return frame.state.players;
  const equipment = new Map(frame.before.players.map((player) => [player.id, [...player.equipment]]));
  for (const event of frame.events.slice(0, through + 1)) {
    const cards = equipment.get(String(event.data?.["player"] ?? ""));
    const id = String(event.data?.["card_id"] ?? "");
    if (!cards || !id) continue;
    if (event.type === "equipment_gained" && !cards.includes(id)) cards.push(id);
    if (event.type === "equipment_lost") {
      const index = cards.indexOf(id);
      if (index >= 0) cards.splice(index, 1);
    }
  }
  return frame.state.players.map((player) => ({ ...player, equipment: equipment.get(player.id) ?? player.equipment }));
}

function scenesFor(frame: PresentationFrame, previousAttack: AttackRoll | null) {
  const scenes: Scene[] = [];
  let attack = previousAttack;
  let attackScene: Scene | undefined;
  const damage = new Map(frame.before?.players.map((player) => [player.id, player.damage]));
  for (const [through, event] of frame.events.entries()) {
    const data = event.data ?? {};
    const player = String(data["player"] ?? "");
    const beforeDamage = damage.get(player);
    if (typeof data["after"] === "number") damage.set(player, data["after"]);
    if (event.type === "game_started" || event.type === "turn_started") attack = null;
    if (event.type === "dice_rolled") {
      attack = data["cause"] === "attack" ? { event, outcomes: [] } : null;
      attackScene = undefined;
    }
    if (attack && (event.type === "damage" || event.type === "heal") &&
        data["cause"] === "attack" && data["by"] === attack.event.data?.["player"]) {
      // Heal.amount can exceed the damage removed; the public before/after
      // counters give the effective recovery without reproducing any rules.
      const outcome = event.type === "heal" && beforeDamage !== undefined && typeof data["after"] === "number"
        ? { ...event, data: { ...data, amount: Math.max(0, beforeDamage - data["after"]) } }
        : event;
      if (!attackScene) {
        attack = { event: attack.event, outcomes: [...attack.outcomes] };
        attackScene = { event: attack.event, cards: null, through, duration: 1650, diceOutcomes: attack.outcomes };
        scenes.push(attackScene);
      }
      attack.outcomes.push(outcome);
      continue;
    }
    if (CARD_EVENTS[event.type]) {
      // Drawing, giving and discarding the same card are separate journeys.
      // Only equipment changes are coalesced, so a loss + gain is one transfer.
      const previous = scenes.at(-1);
      if (event.type.startsWith("equipment_") && previous?.cards?.every((entry) => entry.type.startsWith("equipment_"))) {
        previous.cards.push(event);
        previous.through = through;
      } else scenes.push({ event: null, cards: [event], through, duration: 0 });
      continue;
    }
    let duration = 0;
    if (event.type === "game_started") duration = 1900;
    else if (event.type === "turn_started") duration = 1150;
    else if (event.type === "dice_rolled") duration = 1650;
    else if (event.type === "move" && !event.data?.["d4"] && !event.data?.["d6"]) duration = 1050;
    else if (event.type === "damage" || event.type === "heal") {
      duration = event.data?.["cause"] === "ability" && (event.data["d4"] || event.data["d6"]) ? 1650 : 1050;
    } else if (event.type === "revealed") duration = 1050;
    if (duration) {
      const next: Scene = { event, cards: null, through, duration };
      if (event === attack?.event) {
        next.diceOutcomes = attack.outcomes;
        attackScene = next;
      }
      scenes.push(next);
    }
  }
  const projected = playersAt(frame, frame.events.length - 1);
  if (projected.some((player, index) => {
    const final = frame.state.players[index]!.equipment;
    return player.equipment.length !== final.length || player.equipment.some((card) => !final.includes(card));
  })) {
    // Loot can change ownership without a gained/lost event; it still lands before handover.
    const handover = scenes.findIndex((scene) => scene.event?.type === "turn_started");
    scenes.splice(handover < 0 ? scenes.length : handover, 0, {
      event: null, cards: [], through: frame.events.length - 1, duration: 0, settleEquipment: true,
    });
  }
  return { scenes, attack };
}

function handAt(frame: PresentationFrame, through: number, me: string): CardBrief | null {
  const pending = frame.beforePending?.player === me ? frame.beforePending : null;
  let hand = pending?.private_info?.card ?? pending?.context?.card ?? null;
  for (const event of frame.events.slice(0, through + 1)) {
    const data = event.data ?? {};
    const card = data["card"] as CardBrief | undefined;
    if ((event.type === "card_drawn" || event.type === "klein_drawn") && data["player"] === me) hand = card ?? null;
    if (event.type === "klein_given") {
      if (data["player"] === me) hand = null;
      if (data["target"] === me) hand = card ?? null;
    }
    if ((event.type === "card_resolved" || event.type === "klein_resolved") && data["player"] === me) hand = null;
    if (event.type === "equipment_gained" && data["player"] === me && data["card_id"] === hand?.id) hand = null;
  }
  return hand;
}

/** Authority can advance while bots act; the table never advances beyond its current scene. */
export function useTablePresentation(live: TableSnapshot) {
  const frame = live.frames[0];
  const [cursor, setCursor] = useState({ frame: 0, scene: 0 });
  const [carried, setCarried] = useState<{ roomId: string | null; frameId: number; attack: AttackRoll | null } | null>(null);
  const previousAttack = carried?.roomId === live.roomId && carried.frameId !== frame?.id ? carried.attack : null;
  const presentation = useMemo(() => frame ? scenesFor(frame, previousAttack) : null, [frame, previousAttack]);
  const scenes = presentation?.scenes ?? [];
  const index = cursor.frame === frame?.id ? cursor.scene : 0;
  const scene = scenes[index];
  const busy = frame !== undefined;
  const motionKey = frame && scene?.cards ? `${frame.id}:${index}` : null;
  const advance = useCallback(() => {
    if (!frame || table.getSnapshot().frames[0]?.id !== frame.id) return;
    if (index + 1 < scenes.length) setCursor({ frame: frame.id, scene: index + 1 });
    else {
      setCarried({ roomId: live.roomId, frameId: frame.id, attack: frame.pending ? presentation?.attack ?? null : null });
      table.finishFrame(frame.id);
    }
  }, [frame, index, scenes.length, live.roomId, presentation]);
  const finishCards = useCallback((key: string) => {
    if (key === motionKey) advance();
  }, [motionKey, advance]);

  useEffect(() => {
    if (!frame || scene?.cards) return;
    // Card scenes advance on actual animation completion, never a racing timer.
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(advance, reduced ? Math.min(scene?.duration ?? 0, 450) : scene?.duration ?? 0);
    return () => window.clearTimeout(timer);
  }, [frame, scene, advance]);

  if (!frame) return {
    snap: live, busy,
    event: live.pending && live.state?.phase === "running" && previousAttack?.outcomes.length === 0 ? previousAttack.event : null,
    diceOutcomes: previousAttack?.outcomes ?? NO_EVENTS,
    cardEvents: NO_EVENTS, motionState: live.state, handCard: null, motionKey, finishCards,
  };

  let turn = frame.before?.turn ?? frame.state.turn;
  let round = frame.before?.round ?? frame.state.round;
  let settled = false;
  for (let shown = 0; shown <= index && shown < scenes.length; shown++) {
    const current = scenes[shown]!;
    if (current.settleEquipment) settled = true;
    if (current.event?.type === "turn_started") {
      turn = frame.state.turn;
      round = frame.state.round;
    }
  }
  const event = scene?.event ?? (
    scenes.length === 0 && frame.pending && previousAttack?.outcomes.length === 0 ? previousAttack.event : null
  );
  if (event?.type === "dice_rolled" && event.data?.["cause"] === "setup") {
    turn = { ...turn, player: String(event.data["player"] ?? turn.player), step: "setup" };
  }
  const through = scene?.through ?? -1;
  const players = playersAt(frame, through, settled);
  const state = { ...frame.state, players, turn, round, result: null, phase: "running" };
  const snap: TableSnapshot = {
    ...live, state, seq: frame.seq, priv: frame.priv, pending: frame.pending,
    legal: [], waitingFor: turn.player,
    room: live.room ? { ...live.room, phase: "running", result: null, turn_player: turn.player, turn_deadline: null } : null,
  };
  return {
    snap, busy, event, diceOutcomes: scene?.diceOutcomes ?? NO_EVENTS,
    cardEvents: scene?.cards ?? NO_EVENTS, motionState: state,
    handCard: handAt(frame, through, live.me?.player_id ?? ""), motionKey, finishCards,
  };
}
